import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  getStoreByOwner, 
  getStoreCustomization, 
  updateStoreCustomization
} from '../lib/firebaseService';

export default function StoreTheme() {
  const { firebaseUser } = useAuth();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [formData, setFormData] = useState({
    customColor: '#FFFFFF',
    layout: 'grid'
  });

  useEffect(() => {
    if (firebaseUser) {
      loadData();
    }
  }, [firebaseUser]);

  const loadData = async () => {
    try {
      setPageLoading(true);
      const userStore = await getStoreByOwner(firebaseUser.uid);
      if (userStore) {
        setStore(userStore);
        
        const customization = await getStoreCustomization(userStore.id);
        if (customization) {
          setFormData({
            customColor: customization.customColor || '#FFFFFF',
            layout: customization.layout || 'grid'
          });
        }
      }
    } catch (error) {
      console.error('Məlumatlar yüklənə bilmədi:', error);
    } finally {
      setPageLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!store) {
      toast.error('Əvvəlcə mağaza yaradın');
      return;
    }

    setLoading(true);
    try {
      await updateStoreCustomization(store.id, formData);
      toast.success('Tema və layout saxlanıldı');
    } catch (error) {
      console.error('Xəta:', error);
      toast.error('Əməliyyat uğursuz oldu');
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Yüklənir...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!store) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl">
          <div className="bg-white rounded-xl border border-border p-8 text-center">
            <p className="text-muted-foreground mb-4">Əvvəlcə mağaza yaradın</p>
            <Button onClick={() => window.location.href = '/dashboard/store'}>
              Mağaza Yarat
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Tema və Layout</h1>
          <p className="text-muted-foreground mt-1">Mağazanızın görünüşünü özəlləşdirin</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Rəng Seçimi */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h2 className="text-xl font-semibold mb-2">Arxa Fon Rəngi</h2>
            <p className="text-sm text-muted-foreground mb-4">Mağazanızın arxa fon rəngini seçin</p>
            
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, customColor: '#FFFFFF' })}
                className={`p-6 rounded-xl border-2 transition-all ${
                  formData.customColor === '#FFFFFF' ? 'border-primary bg-primary/5 ring-2 ring-primary ring-offset-2' : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="w-full h-24 bg-white border-2 border-gray-200 rounded-lg mb-3"></div>
                <p className="font-semibold text-base">Ağ Tema</p>
                <p className="text-sm text-muted-foreground mt-1">Klassik və təmiz görünüş</p>
              </button>
              
              <button
                type="button"
                onClick={() => setFormData({ ...formData, customColor: '#000000' })}
                className={`p-6 rounded-xl border-2 transition-all ${
                  formData.customColor === '#000000' ? 'border-primary bg-primary/5 ring-2 ring-primary ring-offset-2' : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="w-full h-24 bg-black rounded-lg mb-3"></div>
                <p className="font-semibold text-base">Qara Tema</p>
                <p className="text-sm text-muted-foreground mt-1">Modern və zərif görünüş</p>
              </button>
            </div>
          </div>

          {/* Layout Seçimi */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h2 className="text-xl font-semibold mb-2">Məhsul Göstəriş Layoutu</h2>
            <p className="text-sm text-muted-foreground mb-4">Məhsullarınızın necə göstəriləcəyini seçin</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                type="button"
                className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${formData.layout === 'grid' ? 'border-primary bg-primary/5 ring-2 ring-primary ring-offset-2' : 'border-border hover:border-primary/50'}`}
                onClick={() => setFormData({ ...formData, layout: 'grid' })}
              >
                <div className="aspect-video bg-gray-100 rounded-lg mb-3 p-3">
                  <div className="grid grid-cols-2 gap-2 h-full">
                    <div className="bg-white rounded-md shadow-sm"></div>
                    <div className="bg-white rounded-md shadow-sm"></div>
                    <div className="bg-white rounded-md shadow-sm"></div>
                    <div className="bg-white rounded-md shadow-sm"></div>
                  </div>
                </div>
                <p className="font-semibold text-base">Grid Layout</p>
                <p className="text-xs text-muted-foreground mt-1">Şəbəkə görünüşü</p>
              </button>

              <button
                type="button"
                className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${formData.layout === 'list' ? 'border-primary bg-primary/5 ring-2 ring-primary ring-offset-2' : 'border-border hover:border-primary/50'}`}
                onClick={() => setFormData({ ...formData, layout: 'list' })}
              >
                <div className="aspect-video bg-gray-100 rounded-lg mb-3 p-3">
                  <div className="flex flex-col gap-2 h-full">
                    <div className="bg-white rounded-md shadow-sm flex-1"></div>
                    <div className="bg-white rounded-md shadow-sm flex-1"></div>
                    <div className="bg-white rounded-md shadow-sm flex-1"></div>
                  </div>
                </div>
                <p className="font-semibold text-base">List Layout</p>
                <p className="text-xs text-muted-foreground mt-1">Siyahı görünüşü</p>
              </button>

              <button
                type="button"
                className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${formData.layout === 'sidebar' ? 'border-primary bg-primary/5 ring-2 ring-primary ring-offset-2' : 'border-border hover:border-primary/50'}`}
                onClick={() => setFormData({ ...formData, layout: 'sidebar' })}
              >
                <div className="aspect-video bg-gray-100 rounded-lg mb-3 p-3">
                  <div className="flex gap-2 h-full">
                    <div className="bg-white rounded-md shadow-sm w-1/4"></div>
                    <div className="bg-white rounded-md shadow-sm flex-1"></div>
                  </div>
                </div>
                <p className="font-semibold text-base">Sidebar Layout</p>
                <p className="text-xs text-muted-foreground mt-1">Yan panel ilə</p>
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={loading} size="lg">
              {loading ? 'Saxlanılır...' : 'Dəyişiklikləri Yadda Saxla'}
            </Button>
          </div>
        </form>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Qeyd:</strong> Dəyişikliklər dərhal mağazanızın public səhifəsində görünəcək.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}
